import React, { Component } from "react";
import { FormControl, Select, MenuItem, Box, Autocomplete, TextField } from "@mui/material";
import AlertDialogPopop from "../../../Component/AlertDialogPopop/AlertDialogPopop";
import { ShowAlert } from "../../../Component/ShowAlert";
import UserAndPermissionService from "../../../Services/UserAndPermissionService";
import CommonPermissonComp from "../../../Component/Permissions/CommonPermissonComp";
import { Permissions } from "../../../Component/Permissions/PermissionsInterface.";
import { getFXReportDefaultPermissions } from '../../../Component/Reusables/Utility';

interface UserPermissionsDialogProps {
    open: boolean;
    onClose?: (state: boolean, action: string) => void;
    accessToken: string;
    onValidationChange: (hasErrors: boolean) => void;
    isPermissionLoading?: boolean
}

export enum ActionButtons {
    Save = 'save',
    Cancel = 'cancel'
}

interface Role {
    role_id: string;
    role_name: string;
}

interface UserPermissionsDialogState {
    roleList: Role[];
    selectedRoleName: string;
    selectedRoleID: string;
    permissions: Permissions;
    loading: boolean;
    isRolesAndPermissionsSettingsSaved: boolean;
    isPermissionLoading: boolean;
    newRoleName: string
    selectedRole: Role | null;
}

class RolesAndPermissionsSettings extends Component<UserPermissionsDialogProps, UserPermissionsDialogState> {

    constructor(props: UserPermissionsDialogProps) {
        super(props);
        this.state = {
            roleList: [],
            selectedRoleName: "",
            selectedRoleID: "",
            loading: false,
            permissions: {
                profileSettings: {
                    isExchangeRateVisible: true,
                    isExchangeRateSaveBtnVisible: true,
                    isGeneralSettingsVisible: true,
                    isGeneralSettingSaveBtnVisible: true,
                    isPauseTransactionVisible: true,
                    isCardVerification: true,
                },
                dashboard: {
                    isVisible: true,
                    isHeaderVisible: true,
                    isTransactionMetricsVisible: true,
                    isFinancialMetricsVisible: true,
                    isRiskMetricsVisible: true,
                    isSupportMetricsVisible: true,
                    isPerformanceMetricsVisible: true,
                },
                transactionLogs: {
                    isVisible: true,
                    isDownloadVisible: true,
                    isFraudMarkVisible: true,
                    isRefundVisible: true,
                },
                userLogs: {
                    isVisible: true,
                },
                complianceLogs: {
                    isVisible: true,
                    isZendeskNavigationAllowed: true,
                    isTransactionNavigationAllowed: true,
                },
                userDetails: {
                    isVisible: true,
                    isVisibleAdminsTab: true,
                    isVisibleMobileUsersTab: true,
                    isVisibleSupportStaffsTab: true,
                    isBlockUserVisible: true,
                    isUserSettingsVisible: true,
                    isUserSettingSaveBtnVisible: true,
                    isStatusHistoryVisible: true,
                    isWalletHistoryTabVisible: true,
                    isUpdatePermissionVisible: true,
                    isDownloadVisibleAdmins: true,
                    isDownloadVisibleMobileUsers: true,
                    isDownloadVisibleSupportStaffs: true,
                    isEditUserInfoVisible: true,
                    isAddUserVisible: true
                },
                userTransactionReport: {
                    isVisible: true,
                    isDownloadVisible: true,
                    isLimitHistoryVisible: true,
                    isLimitHistoryDownloadVisible: true,
                },
                promoCode: {
                    isVisible: true,
                    isDownloadVisiblePromoCode: true,
                    isDownloadVisibleCashbackCoupon: true,
                    isVisiblePromoCodeTab: true,
                    isVisibleCouponCodeTab: true,
                    isAddPromoCodeVisible: true,
                    isAddCouponCodeVisible: true,
                    isBlockCodeVisible: true,
                    isActiveCodeVisible: true
                },
                exchangeRates: {
                    isVisible: true,
                    isDownloadVisible: true,
                    isAddCurrencyPairVisible: true,
                    isEditRatesVisible: true,
                    isEditPaymentMethodVisible: true,
                    isActiveCurrencyAllowed: true,
                    isInactiveCurrencyAllowed: true,
                },
                referrals: {
                    isVisible: true,
                    isDownloadVisible: true,
                    isReferralSettingsVisible: true,
                    isSaveSettingsAllowed: true,
                    isViewRefereesVisible: true,
                    isTransactionNavigationAllowed: true
                },
                chargeBack: {
                    isVisible: true,
                    isDownloadFileAllowed: true,
                    isSendEmailAllowd: true
                },
                partner: {
                    isVisible: true,
                    isVisibleLiquidityTab: true,
                    isVisiblePayoutTab: true,
                    isDownloadVisibleLiquidity: true,
                    isDownloadVisiblePayout: true,
                    isAddVisibleLiquidity: true,
                    isAddVisiblePayout: true,
                    isEditPartnersVisible: true,
                    isActivePartnersVisible: true,
                    isInActivePartnersVisible: true
                },
                ratereconciliation: {
                    isVisible: true,
                },
                fxReports: getFXReportDefaultPermissions(),
                fxAllocation: {
                    isVisible: true,
                    isCreatePurchaseVisible: true,
                    isCreateLiquidityVisible: true,
                    isCreatePartnerVisible: true
                },
            },
            isRolesAndPermissionsSettingsSaved: false,
            isPermissionLoading: false,
            newRoleName: "",
            selectedRole: null
        }
    }

    async componentDidMount() {
        this.getUserRoles();
    }

    // To Update parent component when state changes
    componentDidUpdate(prevProps: Readonly<UserPermissionsDialogProps>, prevState: Readonly<UserPermissionsDialogState>) {
        if (prevState !== this.state) {
            this.notifyValidationStatus();
        }
    }

    // To pass the validation status to the parent component
    notifyValidationStatus = () => {
        if (this.props.onValidationChange) {
            this.props.onValidationChange(this.hasErrors());
        }
    };

    hasErrors = (): boolean => {
        const { selectedRole, newRoleName } = this.state;
        return !selectedRole && !newRoleName; // Disable button only if both are empty
    };

    private async getUserRoles() {
        try {

            const response = await UserAndPermissionService.getUserRoles(this.props.accessToken);

            if (!response.ok) {
                throw new Error("Failed to fetch user roles");
            }
            const data = await response.json();
            this.setState({
                roleList: data.roleList
            });
        } catch (error) {
            ShowAlert("Error", "Failed to fetch user Roles. Please try again later.", "error");
            console.error("Error fetching user roles:", error);
        }
    }

    private async handleClose(action: string) {

        if (action === ActionButtons.Save) {
            try {
                const { selectedRole, newRoleName, permissions } = this.state;
                let postData;
                let response;

                if (selectedRole) {
                    // If an existing role is selected, update permissions
                    postData = {
                        "permissions": {
                            "profileSettings": { ...permissions.profileSettings },
                            "dashboard": { ...permissions.dashboard },
                            "transactionLogs": { ...permissions.transactionLogs },
                            "userLogs": { ...permissions.userLogs },
                            "complianceLogs": { ...permissions.complianceLogs },
                            "userDetails": { ...permissions.userDetails },
                            "userTransactionReport": { ...permissions.userTransactionReport },
                            "promoCode": { ...permissions.promoCode },
                            "exchangeRates": { ...permissions.exchangeRates },
                            "referrals": { ...permissions.referrals },
                            "chargeback": { ...permissions.chargeBack },
                        }
                    };
                    response = await UserAndPermissionService.updateRolePermissions(this.props.accessToken, postData, selectedRole?.role_id);
                } else if (newRoleName) {
                    // If a new role is typed, create a new role with permissions
                    postData = {
                        "userDetails": {
                            "roleName": newRoleName
                        },
                        "permissions": {
                            "profileSettings": { ...permissions.profileSettings },
                            "dashboard": { ...permissions.dashboard },
                            "transactionLogs": { ...permissions.transactionLogs },
                            "userLogs": { ...permissions.userLogs },
                            "complianceLogs": { ...permissions.complianceLogs },
                            "userDetails": { ...permissions.userDetails },
                            "userTransactionReport": { ...permissions.userTransactionReport },
                            "promoCode": { ...permissions.promoCode },
                            "exchangeRates": { ...permissions.exchangeRates },
                            "referrals": { ...permissions.referrals },
                            "chargeback": { ...permissions.chargeBack },
                        }
                    };
                    response = await UserAndPermissionService.createNewRole(this.props.accessToken, postData);
                }

                if (response?.status === 200) {
                    ShowAlert(
                        "Success",
                        selectedRole
                            ? "Permissions updated successfully!"
                            : `New role "${newRoleName}" created successfully!`,
                        "success"
                    );
                    this.getUserRoles();
                    return;
                } else {
                    console.log("Error processing request");
                    throw new Error();
                }
            } catch (error) {
                console.error("Error processing request:", error);
                ShowAlert("Alert", "Failed to Save Changes. Please try again later.", "error");
                return;
            }
        }

        if (this.props.onClose) {
            this.props.onClose(false, action);
        }
    }

    handlePermissionsChange = (updatedPermissions: any) => {
        console.log("Updated Permissions:", updatedPermissions);
        this.setState({ permissions: updatedPermissions });
    };

    // Update new influence value if not in dropdown
    handleInputChange = (inputValue: string) => {
        const sanitizedValue = inputValue
            .replace(/[^a-zA-Z0-9 ]/g, "") // Allow only letters, numbers, and spaces
            .replace(/\b\w/g, (char) => char.toUpperCase()) // Convert to title case
            .slice(0, 30); // Limit to 30 characters

        this.setState({
            newRoleName: sanitizedValue
        });
    };

    handleRoleChange = async (newValue: any) => {
        const selectedRoleName = typeof newValue === "string" ? newValue : newValue?.role_name;
        const selectedRole = this.state.roleList.find(role => role.role_name === selectedRoleName);

        if (selectedRole?.role_id) {
            this.setState({ isPermissionLoading: true });

            try {
                const response = await UserAndPermissionService.getPermissions(this.props.accessToken, selectedRole.role_id);

                if (!response.ok) {
                    throw new Error(`Failed to fetch role details (Status: ${response.status})`);
                }

                const data = await response.json();

                this.setState({ permissions: data?.permissions });
            } catch (error) {
                console.error("Error fetching role details:", error);
            } finally {
                this.setState({ isPermissionLoading: false });
            }
        }

        this.processRoleSelection(selectedRoleName, selectedRole);
    };

    processRoleSelection = (newValue: string, selectedRole: any) => {
        if (typeof newValue === "string" && !selectedRole) {
            // User typed a new role (not selected from options)
            console.log("nwvalue updating");
            this.setState({
                selectedRole: null,
                selectedRoleName: "",
                newRoleName: newValue
            });
        } else {
            // User selected a role from the list
            console.log("Selected from list");
            this.setState({
                selectedRole,
                selectedRoleName: selectedRole?.role_name || "",
                newRoleName: selectedRole?.role_name || ""
            });
        }
    };


    render() {
        return (
            <Box style={{ display: "flex", gap: "25px" }}>
                {/* Left Column - File Components */}
                <Box style={{ flex: 1 }}>
                    <Box style={{ display: "flex", flexDirection: "column", gap: "20px", marginTop: "10px" }}>
                        <FormControl fullWidth>
                            <Autocomplete
                                freeSolo
                                options={this.state.roleList}
                                getOptionLabel={(option) =>
                                    typeof option === "string" ? option : option.role_name
                                }
                                value={this.state.selectedRole || null}
                                inputValue={this.state.newRoleName || ""}
                                onChange={(_, newValue) => this.handleRoleChange(newValue)}
                                onInputChange={(_, newInputValue) => this.handleInputChange(newInputValue)}
                                renderInput={(params) => (
                                    <TextField
                                        {...params}
                                        placeholder="Select or create role"
                                        variant="outlined"
                                    />
                                )}
                            />
                        </FormControl>
                    </Box>
                </Box>

                {/* Right Column - Permissions Component */}
                <Box style={{ flex: 1 }}>
                    <CommonPermissonComp
                        permissions={this.state.permissions}
                        onPermissionsChange={this.handlePermissionsChange}
                        isPermissionLoading={this.state.isPermissionLoading}
                    />
                </Box>
                <AlertDialogPopop
                    open={this.state.isRolesAndPermissionsSettingsSaved}
                    alertTitle='Confirmation'
                    alertBody={`Are you sure you want to save the settings?`}
                    onClose={(state_, action_) => {
                        this.setState({
                            isRolesAndPermissionsSettingsSaved: false
                        }, () => {
                            if (action_ == "ok") {
                                this.handleClose(ActionButtons.Save);
                            }
                        })
                    }}
                />
            </Box>
        );
    }
}

export default RolesAndPermissionsSettings;
